import { useState } from "react";
import { ArrowsClockwise, X } from "@phosphor-icons/react";
import { useMobileLayout } from "@/hooks/use-mobile-layout";

interface SwUpdateBannerProps {
  onRefresh: () => void;
}

/**
 * Notifies the user that a new version of the app has been deployed.
 *
 * - **Desktop**: renders as a slim top bar (pushes content down).
 * - **Mobile/PWA**: renders as a floating pill positioned below the safe-area
 *   notch so it's never obscured by the status bar or Dynamic Island. The pill
 *   is dismissible — the user can close it and continue working without
 *   refreshing (the notification won't re-appear until the next deployment).
 */
export function SwUpdateBanner({ onRefresh }: SwUpdateBannerProps) {
  const isMobile = useMobileLayout();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  if (isMobile) {
    // Floating pill that sits below the notch / safe-area, dismissible
    return (
      <div
        className="pointer-events-none fixed inset-x-0 top-0 z-[9998] flex justify-center"
        style={{ paddingTop: "calc(var(--safe-area-top, 0px) + 0.5rem)" }}
      >
        <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-zinc-700/80 bg-zinc-900/95 px-3 py-1.5 shadow-lg shadow-black/40 backdrop-blur-md">
          <span className="text-[13px] font-medium text-zinc-200">Update available</span>
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[12px] font-semibold text-zinc-100 transition-colors active:bg-white/20"
          >
            <ArrowsClockwise className="size-3" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="flex size-5 items-center justify-center rounded-full text-zinc-500 transition-colors active:bg-white/10 active:text-zinc-300"
            aria-label="Dismiss"
          >
            <X className="size-3" weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  // Desktop: slim top bar that pushes layout down
  return (
    <div className="w-full border-b border-zinc-800 bg-zinc-900/95 px-3 py-1.5 text-zinc-100 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 text-xs sm:text-sm">
        <span className="truncate">New version of the webapp published</span>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-1.5 rounded border border-zinc-700 bg-zinc-800 px-2 py-0.5 font-medium text-zinc-100 hover:bg-zinc-700"
        >
          <ArrowsClockwise className="size-3.5" />
          <span>Refresh</span>
        </button>
      </div>
    </div>
  );
}
